import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {getPydanticError} from '~/common/utils/errors';
import {PostContextProvider} from '~/ViewInvitation/components/Guestlist/context/PostContext';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {usePostContext} from '~/ViewInvitation/components/Guestlist/hooks/usePostContext';
import {usePostsApi} from '~/ViewInvitation/components/Guestlist/hooks/usePostsApi';
import {Comment} from '~/ViewInvitation/components/Guestlist/Post/Comment';
import {Reply} from '~/ViewInvitation/components/Guestlist/Post/Reply';
import {ReplyForm} from '~/ViewInvitation/components/Guestlist/Post/ReplyForm';
import type {ReplyResponse, Reply as ReplyType} from '~/ViewInvitation/types/api';
import type {Guest} from '~/ViewInvitation/types/guests';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

import * as styles from './Post.module.scss';

interface PostProps {
  guest: Guest;
}

const PostContent = memo(({guest}: PostProps) => {
  const [repliesAnimationRef] = useAutoAnimate();
  const [formAnimationRef] = useAutoAnimate();
  const {
    state: {all: guests},
  } = useGuestlistDataContext();
  const {setIsEditing: setIsPostEditing} = usePostContext();
  const {
    getPostsForGuest,
    isLoading,
    status: {toggleLike},
  } = usePostsApi();
  const post = useMemo(() => getPostsForGuest(guest.userId)?.at(0), [getPostsForGuest, guest]);

  const [isReplying, setIsReplying] = useState(false);

  const replies = post?.replies.map(
    (reply: ReplyType) => ({
      guest: guests.find((g) => g.userId === reply.userId),
      reply,
    }),
    [guests]
  );

  const resetEditingState = useCallback(() => {
    setIsPostEditing(false);
    setIsReplying(false);
  }, [setIsPostEditing]);

  useEffect(
    () => () => {
      resetEditingState();
    },
    [resetEditingState]
  );

  const onReply = useCallback(
    (response: ReplyResponse | Error) => {
      if (response instanceof Error) {
        evite.error('Failed to reply to post', response);
        const message = getPydanticError(response, '');
        toastAlert(`Unable to post reply.\n${message}`, {
          icon: <AlertLineIcon />,
          type: 'error',
        });
      } else {
        resetEditingState();
      }
    },
    [resetEditingState]
  );

  const showEditing = useCallback(() => {
    setIsPostEditing(true);
    setIsReplying(true);
  }, [setIsPostEditing]);

  if (isLoading && !toggleLike.isLoading)
    return (
      <div className={styles.activity}>
        <Typography variant="label2" color="pebble">
          Activity
        </Typography>
        <Skeleton height={75} />
      </div>
    );

  if (!post) return null;

  return (
    <div className={styles.activity}>
      <Typography variant="label2" color="pebble">
        Activity
      </Typography>
      <div ref={repliesAnimationRef} className={styles.post}>
        <div
          className={cx(styles.commentAndForm, {[styles.isReplying]: isReplying})}
          ref={formAnimationRef}
        >
          <Comment
            guest={guest}
            post={post}
            onReply={showEditing}
            isReplying={isReplying}
            onCancel={resetEditingState}
          />
          {isReplying && (
            <ReplyForm onReply={onReply} post={post} closeEditor={resetEditingState} />
          )}
        </div>
        {replies?.map((reply) => (
          <Reply key={reply.reply.id} guest={reply.guest!} reply={reply.reply} />
        ))}
      </div>
    </div>
  );
});

PostContent.displayName = 'PostContent';

export const Post = memo(({guest}: PostProps) => (
  <PostContextProvider>
    <PostContent guest={guest} />
  </PostContextProvider>
));

Post.displayName = 'Post';
